import { createBrowserRouter, Navigate } from "react-router";
import { PlayLayout, RootLayout, WorkspaceLayout } from "../layouts/layouts";
import { RouteErrorState } from "@/components/feedback/states";
import { RoleGuard } from "@/features/authentication/route-guard";
import type { PrincipalKind } from "@/features/authentication/auth-context";
import { AboutPage, JoinPage, LandingPage, NotFoundPage, PrivacyPage } from "@/routes/public/pages";
import { PlaceholderPage } from "@/routes/placeholders";

const placeholder = (title: string) =>
  function PlaceholderRoute() {
    return <PlaceholderPage title={title} />;
  };
const workspace = (label: string, roles: PrincipalKind[]) =>
  function GuardedWorkspace() {
    return (
      <RoleGuard allow={roles}>
        <WorkspaceLayout label={label} />
      </RoleGuard>
    );
  };
const studentLayout = () => (
  <RoleGuard allow={["participant"]}>
    <PlayLayout />
  </RoleGuard>
);

const developmentRoutes = import.meta.env.DEV
  ? [
      {
        path: "dev/design-system",
        lazy: async () => ({
          Component: (await import("@/routes/dev/design-system-page")).default,
        }),
      },
      {
        path: "dev/mapping",
        lazy: async () => ({
          Component: (await import("@/features/article-mapping/components/mapping-activity"))
            .default,
        }),
      },
    ]
  : [];

export const router = createBrowserRouter([
  {
    Component: RootLayout,
    ErrorBoundary: RouteErrorState,
    children: [
      { index: true, Component: LandingPage },
      { path: "about", Component: AboutPage },
      { path: "privacy", Component: PrivacyPage },
      { path: "join", Component: JoinPage },
      { path: "join/:sessionCode", Component: JoinPage },
      {
        path: "login",
        lazy: async () => ({
          Component: (await import("@/features/authentication/login-page")).default,
        }),
      },
      {
        path: "auth/accept-invite",
        lazy: async () => ({
          Component: (await import("@/features/authentication/account-setup-pages"))
            .AcceptInvitePage,
        }),
      },
      {
        path: "auth/recover",
        lazy: async () => ({
          Component: (await import("@/features/authentication/account-setup-pages"))
            .RecoverAccountPage,
        }),
      },
      {
        path: "access-disabled",
        lazy: async () => ({
          Component: (await import("@/features/authentication/account-setup-pages"))
            .AccessDisabledPage,
        }),
      },
      {
        path: "constitution",
        lazy: async () => ({
          Component: (await import("@/features/constitution/components/constitution-explorer"))
            .default,
        }),
      },
      {
        path: "play/:sessionId",
        Component: studentLayout,
        children: [
          { index: true, element: <Navigate to="welcome" replace /> },
          {
            path: "consent",
            lazy: async () => ({
              Component: (await import("@/features/participants/components/student-pages"))
                .ConsentPage,
            }),
          },
          {
            path: "welcome",
            lazy: async () => ({
              Component: (await import("@/features/participants/components/student-pages"))
                .StudentWelcomePage,
            }),
          },
          {
            path: "pre-test",
            lazy: async () => ({
              Component: (await import("@/features/assessments/components/assessment-player"))
                .PreTestPage,
            }),
          },
          { path: "module/:moduleId", Component: placeholder("Learning module") },
          {
            path: "learn/constitution",
            lazy: async () => ({
              Component: (await import("@/features/participants/components/student-learning-pages"))
                .StudentConstitutionPage,
            }),
          },
          {
            path: "scenario/:scenarioId",
            lazy: async () => ({
              Component: (await import("@/features/scenarios/components/scenario-player")).default,
            }),
          },
          { path: "flashcards/:deckId", Component: placeholder("Flashcards") },
          {
            path: "mapping/:activityId",
            lazy: async () => ({
              Component: (await import("@/features/participants/components/student-learning-pages"))
                .StudentMappingPage,
            }),
          },
          {
            path: "simulation/:simulationId",
            lazy: async () => ({
              Component: (await import("@/features/simulations/components/phaser-simulation"))
                .default,
            }),
          },
          { path: "reflection", Component: placeholder("Reflection") },
          {
            path: "post-test",
            lazy: async () => ({
              Component: (await import("@/features/assessments/components/assessment-player"))
                .PostTestPage,
            }),
          },
        ],
      },
      {
        path: "teacher",
        Component: workspace("Teacher", ["teacher"]),
        children: [
          { index: true, element: <Navigate to="sessions" replace /> },
          { path: "dashboard", element: <Navigate to="/teacher/sessions" replace /> },
          {
            path: "classes",
            lazy: async () => ({
              Component: (await import("@/features/classes/class-pages")).ClassesPage,
            }),
          },
          {
            path: "classes/:classId",
            lazy: async () => ({
              Component: (await import("@/features/classes/class-pages")).ClassDetailPage,
            }),
          },
          {
            path: "sessions",
            lazy: async () => ({
              Component: (await import("@/features/learning-sessions/session-pages")).SessionsPage,
            }),
          },
          {
            path: "sessions/:sessionId",
            lazy: async () => ({
              Component: (await import("@/features/learning-sessions/session-pages"))
                .SessionDetailPage,
            }),
          },
          { path: "content", Component: placeholder("Content library") },
          { path: "assessments", Component: placeholder("Assessments") },
          { path: "questions", Component: placeholder("Questions") },
          { path: "flashcards", Component: placeholder("Flashcards") },
          { path: "scenarios", Component: placeholder("Scenarios") },
          {
            path: "scenarios/:scenarioId/editor",
            lazy: async () => ({
              Component: (await import("@/features/scenarios/editor/scenario-editor")).default,
            }),
          },
        ],
      },
      {
        path: "researcher",
        Component: workspace("Researcher", ["researcher"]),
        children: [
          { index: true, element: <Navigate to="studies" replace /> },
          { path: "dashboard", element: <Navigate to="/researcher/studies" replace /> },
          {
            path: "studies",
            lazy: async () => ({
              Component: (await import("@/features/research-analytics/research-pages"))
                .ResearchStudiesPage,
            }),
          },
          {
            path: "studies/:studyId",
            lazy: async () => ({
              Component: (await import("@/features/research-analytics/research-pages"))
                .ResearchStudyDetailPage,
            }),
          },
          { path: "datasets", Component: placeholder("Pseudonymised datasets") },
          { path: "analytics", Component: placeholder("Research analytics") },
          { path: "exports", Component: placeholder("Research exports") },
        ],
      },
      {
        path: "admin",
        Component: workspace("Administrator", ["administrator"]),
        children: [
          { index: true, element: <Navigate to="organisations" replace /> },
          { path: "organisations", Component: placeholder("Organisations") },
          { path: "users", Component: placeholder("Users") },
          { path: "content", Component: placeholder("Content governance") },
          { path: "audit-logs", Component: placeholder("Audit logs") },
        ],
      },
      {
        path: "platform",
        Component: workspace("Platform", ["superadmin"]),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          {
            path: "dashboard",
            lazy: async () => ({
              Component: (await import("@/features/platform-administration/platform-admin-pages"))
                .PlatformDashboardPage,
            }),
          },
          {
            path: "organisations",
            lazy: async () => ({
              Component: (await import("@/features/platform-administration/platform-admin-pages"))
                .PlatformOrganisationsPage,
            }),
          },
          {
            path: "users",
            lazy: async () => ({
              Component: (await import("@/features/platform-administration/platform-admin-pages"))
                .PlatformUsersPage,
            }),
          },
          {
            path: "audit",
            lazy: async () => ({
              Component: (await import("@/features/platform-administration/platform-admin-pages"))
                .PlatformAuditPage,
            }),
          },
        ],
      },
      ...developmentRoutes,
      { path: "*", Component: NotFoundPage },
    ],
  },
]);
